import React, { useMemo, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import TimeRangePicker from '../../Common/TimeRangePicker';
import SpaceSelect from '../../Common/SpaceSelect';
import PriceCalendar from '../../VenueDetails/PriceCalendar';
import VenueHostInfo from '../../VenueDetails/VenueHostInfo';
import CostSummary from './CostSummary';
import Pagination from '../../SearchResults/components/Pagination';
import venueDashboardApiService from '../../../services/venueDashboardApi';
import { getNextAvailableStartTime, getBlockedTimesFromOperatingHoursAndHolidays, mergeOperatingHoursForBookingMode, pickFirstValidEndAfterStart, buildMergedBlockedStartTimes, buildMergedBlockedEndTimesForStart } from '../../VenueDashboard/utils/bookingUtils';
import { getPastTimesForDate, parseDateOnly } from '../../VenueDashboard/utils/dateUtils';
import {
  CheckCircleIcon
} from '../../icons';
import BulletSparkIcon from '../../icons/BulletSparkIcon';
import {
  ADDON_PRICING_UNITS,
  getAddonPricingUnit,
  getAddonUnitLabel
} from '../../../utils/addonPricing';
import { normalizeTagSlugs } from '../../../utils/catalogTags';
import { getBookingHostRulesText, getBookingCancellationPolicyText } from '../../../utils/bookingHostRules';
import {
  bookingDepositDisclaimerLines,
  computeVenueDepositQuote,
} from '../../../utils/venueDeposits';

/** Align with 30m slot keys in blockedTimes (HH:MM), strip seconds if present */
const toBookingTimeSlot = (t) => {
  if (t == null || t === '') return '';
  const m = String(t).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return String(t);
  return `${m[1].padStart(2, '0')}:${m[2]}`;
};

const ADDONS_PER_PAGE = 6;

const ADDON_TABS = {
  PACKAGES: 'packages',
  ADDONS: 'addons',
  OTHERS: 'others',
};

const BOOKING_FORM_ID = 'booking-request-form';

const BOOKING_STEPS = {
  EVENT: 'event',
  ADDONS: 'addons',
  ORGANISER: 'organiser',
};

const BOOKING_STEP_ORDER = [BOOKING_STEPS.EVENT, BOOKING_STEPS.ADDONS, BOOKING_STEPS.ORGANISER];

const BOOKING_STEP_LABELS = {
  [BOOKING_STEPS.EVENT]: 'Event details',
  [BOOKING_STEPS.ADDONS]: 'Packages & Add-ons',
  [BOOKING_STEPS.ORGANISER]: 'Organiser details',
};

const BOOKING_STEP_TITLES = {
  [BOOKING_STEPS.EVENT]: 'Tell us about your event',
  [BOOKING_STEPS.ADDONS]: 'Add anything else you need',
  [BOOKING_STEPS.ORGANISER]: 'How can the venue reach you?',
};

const STEP_FOR_ERROR = {
  date: BOOKING_STEPS.EVENT,
  duration: BOOKING_STEPS.EVENT,
  recurringSchedule: BOOKING_STEPS.EVENT,
  dietaryRestrictions: BOOKING_STEPS.EVENT,
  name: BOOKING_STEPS.ORGANISER,
  email: BOOKING_STEPS.ORGANISER,
  phone: BOOKING_STEPS.ORGANISER,
  terms: BOOKING_STEPS.ORGANISER,
};

const AddonQuantityStepper = ({ value, max, disabled, ariaLabel, onChange }) => {
  const current = Number.isFinite(Number(value)) ? Number(value) : 0;
  const commit = (next) => onChange(Math.min(Math.max(0, next), max));

  if (current <= 0) {
    return (
      <button
        type="button"
        className="addon-add-button"
        disabled={disabled || max < 1}
        onClick={() => commit(1)}
      >
        <img className="addon-add-plus" src="/manual-booking/icons/add.png" alt="" />
        Add
      </button>
    );
  }

  return (
    <div className="booking-guest-stepper addon-qty-stepper">
      <input
        type="number"
        className="form-input"
        min={0}
        max={max}
        step={1}
        value={value}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(event) => {
          const parsed = parseInt(event.target.value, 10);
          commit(Number.isFinite(parsed) ? parsed : 0);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.preventDefault();
        }}
      />
      <div className="booking-guest-stepper-actions">
        <button
          type="button"
          className="booking-guest-stepper-btn"
          aria-label={`Decrease ${ariaLabel}`}
          disabled={disabled || current <= 0}
          onClick={() => commit(current - 1)}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M3.5 8H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
        <button
          type="button"
          className="booking-guest-stepper-btn"
          aria-label={`Increase ${ariaLabel}`}
          disabled={disabled || current >= max}
          onClick={() => commit(current + 1)}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 3.5V12.5M3.5 8H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const BookingForm = ({
  acceptTerms,
  availabilityPreview,
  bookingData,
  currentUser,
  errors,
  isSubmitting,
  onAcceptTermsChange,
  onInputChange,
  onSubmit,
  pricingDetails,
  submitError,
  venue,
  venueSpaces = [],
  packages,
  packageQuantities,
  onPackageQuantityChange,
  addOnItems = [],
  addOnItemQuantities = {},
  onAddOnQuantityChange,
  otherItems = [],
  otherItemQuantities = {},
  onOtherQuantityChange,
  bookingRules,
  operatingHours = {},
  operatingHoursPerPerson = {},
  holidays = [],
  hidePrice = false,
  dynamicPricing = [],
  dynamicPricingPerPerson = [],
  defaultPerPersonPrice = null,
  flatPricing = [],
  catalogTags = [],
  catalogLoading = false,
  submitSlot = null,
  stripePaymentsUiEnabled = false,
}) => {
  const startTimeInputId = useMemo(() => 'start-time', []);
  const endTimeInputId = useMemo(() => 'end-time', []);
  const [blocks, setBlocks] = useState([]);
  const [loadingBusyTimes, setLoadingBusyTimes] = useState(false);
  const [showFullHostRules, setShowFullHostRules] = useState(false);
  const [activeAddonTab, setActiveAddonTab] = useState(ADDON_TABS.PACKAGES);
  const [selectedTagSlug, setSelectedTagSlug] = useState('');
  const [addonPage, setAddonPage] = useState(1);
  const [activeStep, setActiveStep] = useState(BOOKING_STEPS.EVENT);
  const [completedSteps, setCompletedSteps] = useState([]);
  const scrollStepIntoViewRef = useRef(false);
  const stepsRef = useRef(null);

  useEffect(() => {
    const tabs = stepsRef.current;
    if (!tabs) return undefined;
    const update = () => {
      const header = document.querySelector('.l2-header-bar');
      const headerBottom = header?.getBoundingClientRect().bottom ?? 64;
      tabs.style.top = `${headerBottom}px`;
      const stuck = tabs.getBoundingClientRect().top <= headerBottom + 1;
      tabs.classList.toggle('is-stuck', stuck);

      const view = document.documentElement.clientWidth;
      const isMobile = view <= 1023;

      if (!isMobile && !stuck) {
        tabs.style.width = '';
        tabs.style.marginLeft = '';
        tabs.style.paddingLeft = '';
        tabs.style.paddingRight = '';
        tabs.style.paddingTop = '';
        tabs.style.paddingBottom = '';
        document.documentElement.style.removeProperty('--booking-steps-offset');
        return;
      }

      const parent = tabs.parentElement;
      const parentStyle = parent ? getComputedStyle(parent) : null;
      const parentRect = parent?.getBoundingClientRect();
      const padLeft = parentStyle ? parseFloat(parentStyle.paddingLeft) || 0 : 0;
      const padRight = parentStyle ? parseFloat(parentStyle.paddingRight) || 0 : 0;
      const contentLeft = (parentRect?.left ?? 0) + padLeft;
      const contentWidth = parent
        ? parent.clientWidth - padLeft - padRight
        : tabs.getBoundingClientRect().width;
      tabs.style.width = `${view}px`;
      tabs.style.marginLeft = `${-contentLeft}px`;
      tabs.style.paddingLeft = `${contentLeft}px`;
      tabs.style.paddingRight = `${Math.max(0, view - contentLeft - contentWidth)}px`;
      tabs.style.paddingTop = '';
      tabs.style.paddingBottom = '';

      if (!stuck) {
        document.documentElement.style.removeProperty('--booking-steps-offset');
        return;
      }

      document.documentElement.style.setProperty(
        '--booking-steps-offset',
        `${headerBottom + tabs.offsetHeight}px`
      );
    };
    update();
    const header = document.querySelector('.l2-header-bar');
    const observer = new ResizeObserver(update);
    if (header) observer.observe(header);
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    const errorStep = BOOKING_STEP_ORDER.find((step) =>
      Object.keys(errors || {}).some((key) => errors[key] && STEP_FOR_ERROR[key] === step)
    );
    if (errorStep) setActiveStep(errorStep);
  }, [errors]);

  useLayoutEffect(() => {
    if (!scrollStepIntoViewRef.current) return;
    scrollStepIntoViewRef.current = false;
    const tabs = stepsRef.current;
    if (!tabs) return;
    const headerBottom = document.querySelector('.l2-header-bar')?.getBoundingClientRect().bottom ?? 0;
    if (tabs.getBoundingClientRect().top < headerBottom) {
      tabs.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
  }, [activeStep]);

  const hasPackages = (packages || []).length > 0;
  const hasAddOns = (addOnItems || []).length > 0;
  const hasOthers = (otherItems || []).length > 0;
  const hasAnyExtras = hasPackages || hasAddOns || hasOthers;

  const visibleAddonTabs = useMemo(() => {
    const tabs = [];
    if (hasPackages) tabs.push(ADDON_TABS.PACKAGES);
    if (hasAddOns) tabs.push(ADDON_TABS.ADDONS);
    if (hasOthers) tabs.push(ADDON_TABS.OTHERS);
    return tabs;
  }, [hasPackages, hasAddOns, hasOthers]);

  const activeTabItemsUnfiltered = useMemo(() => {
    if (activeAddonTab === ADDON_TABS.PACKAGES) return packages || [];
    if (activeAddonTab === ADDON_TABS.ADDONS) return addOnItems || [];
    if (activeAddonTab === ADDON_TABS.OTHERS) return otherItems || [];
    return [];
  }, [activeAddonTab, packages, addOnItems, otherItems]);

  const tagFiltersForTab = useMemo(() => {
    const slugsInTab = new Set();
    activeTabItemsUnfiltered.forEach((item) => {
      normalizeTagSlugs(item.tags).forEach((slug) => slugsInTab.add(slug));
    });
    return (catalogTags || []).filter(
      (tag) => tag?.is_active !== false && slugsInTab.has(String(tag.slug).toLowerCase())
    );
  }, [activeTabItemsUnfiltered, catalogTags]);

  const activeTabItems = useMemo(() => {
    if (!selectedTagSlug) return activeTabItemsUnfiltered;
    return activeTabItemsUnfiltered.filter((item) =>
      normalizeTagSlugs(item.tags).includes(selectedTagSlug)
    );
  }, [activeTabItemsUnfiltered, selectedTagSlug]);

  const totalAddonPages = Math.max(1, Math.ceil(activeTabItems.length / ADDONS_PER_PAGE));
  const addonStartIndex = (addonPage - 1) * ADDONS_PER_PAGE;
  const addonEndIndex = Math.min(addonStartIndex + ADDONS_PER_PAGE, activeTabItems.length);
  const paginatedTabItems = activeTabItems.slice(addonStartIndex, addonEndIndex);

  useEffect(() => {
    if (catalogLoading) return;
    if (!visibleAddonTabs.includes(activeAddonTab)) {
      setActiveAddonTab(visibleAddonTabs[0] || ADDON_TABS.PACKAGES);
    }
  }, [activeAddonTab, visibleAddonTabs, catalogLoading]);

  useEffect(() => {
    setSelectedTagSlug('');
  }, [activeAddonTab]);

  useEffect(() => {
    if (selectedTagSlug && !tagFiltersForTab.some((tag) => tag.slug === selectedTagSlug)) {
      setSelectedTagSlug('');
    }
  }, [selectedTagSlug, tagFiltersForTab]);

  useEffect(() => {
    setAddonPage(1);
  }, [activeAddonTab, packages, addOnItems, otherItems, selectedTagSlug]);

  useEffect(() => {
    if (addonPage > totalAddonPages) {
      setAddonPage(totalAddonPages);
    }
  }, [addonPage, totalAddonPages]);

  const isFullVenue = bookingData?.bookingType === 'full-venue';
  const minHours = isFullVenue
    ? (bookingRules?.min_hours_exclusive ?? bookingRules?.min_total_hours ?? 2)
    : (bookingRules?.min_hours_per_person ?? 2);

  const selectedSpace = useMemo(() => {
    if (!bookingData?.spaceId || !venueSpaces?.length) return null;
    return venueSpaces.find(
      (space) => String(space.id || space.space_id || space.spaceId) === String(bookingData.spaceId)
    );
  }, [venueSpaces, bookingData?.spaceId]);

  const maxAttendees = useMemo(() => {
    if (isFullVenue) {
      const fromSpace =
        selectedSpace?.capacity_total ??
        selectedSpace?.capacityTotal ??
        selectedSpace?.capacity ??
        availabilityPreview?.capacity_total ??
        venue?.max_capacity ??
        venue?.capacity ??
        venue?.maxCapacity;
      if (fromSpace != null && Number.isFinite(Number(fromSpace))) {
        return Math.max(1, Number(fromSpace));
      }
      return null;
    }
    const fromPreview =
      availabilityPreview?.remaining_capacity ?? availabilityPreview?.capacity_total;
    if (fromPreview != null && Number.isFinite(Number(fromPreview))) {
      return Math.max(0, Number(fromPreview));
    }
    const fromSpace =
      selectedSpace?.capacity_total ??
      selectedSpace?.capacityTotal ??
      selectedSpace?.capacity ??
      selectedSpace?.tables_available ??
      selectedSpace?.tablesAvailable;
    if (fromSpace != null && Number.isFinite(Number(fromSpace))) {
      return Math.max(1, Number(fromSpace));
    }
    const fromVenue = venue?.max_capacity ?? venue?.capacity ?? venue?.maxCapacity;
    if (fromVenue != null && Number.isFinite(Number(fromVenue))) {
      return Math.max(1, Number(fromVenue));
    }
    return null;
  }, [
    isFullVenue,
    availabilityPreview?.remaining_capacity,
    availabilityPreview?.capacity_total,
    selectedSpace,
    venue?.max_capacity,
    venue?.capacity,
    venue?.maxCapacity,
  ]);

  const attendeesCount = Math.max(0, parseInt(bookingData?.attendees, 10) || 0);
  const addonQtyMax = attendeesCount * 3;

  const eventDurationHours = useMemo(() => {
    const start = bookingData?.startTime;
    const end = bookingData?.endTime;
    if (!start || !end) return 1;
    const startMatch = String(start).match(/^(\d{1,2}):(\d{2})/);
    const endMatch = String(end).match(/^(\d{1,2}):(\d{2})/);
    if (!startMatch || !endMatch) return 1;
    const startMinutes = Number(startMatch[1]) * 60 + Number(startMatch[2]);
    const endMinutes = Number(endMatch[1]) * 60 + Number(endMatch[2]);
    const diff = endMinutes - startMinutes;
    return Math.max(1, Math.ceil(diff / 60));
  }, [bookingData?.startTime, bookingData?.endTime]);

  const getOtherQtyMax = (item) => {
    const unit = getAddonPricingUnit(item);
    if (unit === ADDON_PRICING_UNITS.PER_EVENT) return 1;
    if (unit === ADDON_PRICING_UNITS.PER_HOUR) return eventDurationHours;
    return addonQtyMax;
  };

  useEffect(() => {
    if (maxAttendees == null) return;
    const current = parseInt(bookingData?.attendees, 10);
    if (Number.isFinite(current) && maxAttendees > 0 && current > maxAttendees) {
      onInputChange('attendees', String(maxAttendees));
    }
  }, [maxAttendees, bookingData?.attendees, onInputChange]);

  // Calculate minimum end time (start time + min hours for booking mode)
  const calculateMinEndTime = (startTime) => {
    if (!startTime) return null;
    
    const [hours, minutes] = startTime.split(':').map(Number);
    const startMinutes = hours * 60 + minutes;
    const minEndMinutes = startMinutes + Math.round(minHours * 60);
    
    // Handle overflow past midnight
    if (minEndMinutes >= 24 * 60) {
      return '23:59'; // Cap at end of day
    }
    
    const minEndHours = Math.floor(minEndMinutes / 60);
    const minMins = minEndMinutes % 60;
    return `${minEndHours.toString().padStart(2, '0')}:${minMins.toString().padStart(2, '0')}`;
  };

  // Fetch blocks from unified availability API (mode=blocks): calendar + manual + capacity-exhausted
  useEffect(() => {
    const fetchBlocks = async () => {
      const venueId = venue?.id ? String(venue.id) : null;
      const spaceId = bookingData.spaceId && !String(bookingData.spaceId).includes('{') ? bookingData.spaceId : null;
      const dateStr = bookingData.date;
      if (!venueId || !spaceId || !dateStr) {
        setBlocks([]);
        return;
      }

      setLoadingBusyTimes(true);
      try {
        const apiBookingMode = bookingData.bookingType === 'full-venue' ? 'exclusive' : 'per_person';
        const rawBlocks = await venueDashboardApiService.getSpaceAvailabilityBlocks(venueId, spaceId, dateStr, apiBookingMode);
        setBlocks(rawBlocks || []);
      } catch {
        setBlocks([]);
      } finally {
        setLoadingBusyTimes(false);
      }
    };

    fetchBlocks();
  }, [venue?.id, bookingData.spaceId, bookingData.date, bookingData.bookingType]);

  const selectedDate = useMemo(
    () => (bookingData.date ? parseDateOnly(String(bookingData.date).split('T')[0]) : null),
    [bookingData.date]
  );
  const venueTimezone = venue?.timezone || venue?.time_zone || 'Europe/London';
  const bookingDateYmd = bookingData?.date && String(bookingData.date).split('T')[0];
  const pastTimes = useMemo(
    () => getPastTimesForDate(selectedDate, venueTimezone, bookingDateYmd || undefined),
    [selectedDate, venueTimezone, bookingDateYmd]
  );
  const effectiveOperatingHours = useMemo(
    () => mergeOperatingHoursForBookingMode(bookingData?.bookingType, operatingHours, operatingHoursPerPerson),
    [bookingData?.bookingType, operatingHours, operatingHoursPerPerson]
  );
  const ohBlockOpts = useMemo(
    () => ({ timezone: venueTimezone, dateYmd: bookingDateYmd || undefined }),
    [venueTimezone, bookingDateYmd]
  );
  const operatingHoursBlocked = useMemo(
    () => getBlockedTimesFromOperatingHoursAndHolidays(selectedDate, effectiveOperatingHours, holidays, ohBlockOpts),
    [selectedDate, effectiveOperatingHours, holidays, ohBlockOpts]
  );
  const operatingHoursBlockedEnd = useMemo(
    () => getBlockedTimesFromOperatingHoursAndHolidays(selectedDate, effectiveOperatingHours, holidays, {
      inclusiveClosingSlot: true,
      ...ohBlockOpts
    }),
    [selectedDate, effectiveOperatingHours, holidays, ohBlockOpts]
  );
  const blockedTimes = useMemo(() => {
    if (!selectedDate) return [];
    return buildMergedBlockedStartTimes({
      blocks,
      bookingType: bookingData.bookingType,
      date: selectedDate,
      venueTimezone,
      selectedDateYmd: bookingDateYmd || undefined,
      operatingHoursBlocked,
      pastTimes,
      minHours,
      operatingHoursBlockedEnd
    });
  }, [blocks, bookingData.bookingType, selectedDate, pastTimes, operatingHoursBlocked, operatingHoursBlockedEnd, venueTimezone, bookingDateYmd, minHours]);
  const blockedEndTimes = useMemo(() => {
    if (!selectedDate || !bookingData.startTime) return [];
    return buildMergedBlockedEndTimesForStart({
      startTime: bookingData.startTime,
      blocks,
      bookingType: bookingData.bookingType,
      date: selectedDate,
      venueTimezone,
      selectedDateYmd: bookingDateYmd || undefined,
      operatingHoursBlockedEnd,
      pastTimes
    });
  }, [blocks, bookingData.bookingType, selectedDate, bookingData.startTime, operatingHoursBlockedEnd, pastTimes, venueTimezone, bookingDateYmd]);

  const startTimeSlot = toBookingTimeSlot(bookingData.startTime);
  const nextAvailableStart = getNextAvailableStartTime(blockedTimes, bookingData.startTime);
  const hasNoAvailableBlock = bookingData.bookingType === 'full-venue' && getNextAvailableStartTime(blockedTimes, '00:00') === null;
  const apiSaysAvailable = availabilityPreview?.is_available === true;
  const clientSaysOutsideHours =
    (startTimeSlot && operatingHoursBlocked.includes(startTimeSlot)) ||
    operatingHoursBlocked.includes(bookingData.startTime);
  const apiBlockingReasons = availabilityPreview?.blocking_reasons || [];
  const filteredBlockingReasons = apiBlockingReasons.filter((r) => {
    const s = String(r);
    if (s === 'outside_operating_hours') return !apiSaysAvailable && clientSaysOutsideHours;
    return true;
  });
  if (clientSaysOutsideHours && !apiBlockingReasons.some((r) => String(r) === 'outside_operating_hours') && !apiSaysAvailable) {
    filteredBlockingReasons.push('outside_operating_hours');
  }
  /** Include client-side outside-hours even when availability preview has not loaded yet (so the button greys out). */
  const userFacingBlockingReasons = [...filteredBlockingReasons];
  if (clientSaysOutsideHours && !userFacingBlockingReasons.some((r) => String(r) === 'outside_operating_hours')) {
    userFacingBlockingReasons.push('outside_operating_hours');
  }
  const cannotBookThisSlot = hasNoAvailableBlock || userFacingBlockingReasons.length > 0;

  const lastAppliedDefaultKeyRef = useRef(null);
  useEffect(() => {
    lastAppliedDefaultKeyRef.current = null;
  }, [bookingData.date, bookingData.spaceId, bookingData.bookingType]);

  // After date (or space / booking mode) changes, default to first available start only when
  // there is no current time or the current time is blocked — preserve deep-link / venue-details times.
  useLayoutEffect(() => {
    if (!bookingData.date || loadingBusyTimes) return;
    const key = `${bookingData.date}|${bookingData.spaceId}|${bookingData.bookingType}`;
    if (lastAppliedDefaultKeyRef.current === key) return;
    const first = getNextAvailableStartTime(blockedTimes, '00:00');
    if (!first) {
      lastAppliedDefaultKeyRef.current = key;
      return;
    }
    const slot = startTimeSlot;
    const startIsBlocked =
      !slot ||
      blockedTimes.includes(slot) ||
      blockedTimes.includes(bookingData.startTime);
    if (!startIsBlocked) {
      lastAppliedDefaultKeyRef.current = key;
      return;
    }
    if (bookingData.startTime === first) {
      lastAppliedDefaultKeyRef.current = key;
      return;
    }
    onInputChange('startTime', first);
    lastAppliedDefaultKeyRef.current = key;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onInputChange from parent
  }, [
    bookingData.date,
    bookingData.spaceId,
    bookingData.bookingType,
    bookingData.startTime,
    startTimeSlot,
    loadingBusyTimes,
    blockedTimes,
    onInputChange
  ]);

  // If the selected start is blocked for this date (hours, calendar, past), move to the first available slot (both booking modes).
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onInputChange intentionally omitted (parent callback not stable)
  useEffect(() => {
    if (!nextAvailableStart) return;
    const blocked =
      blockedTimes.includes(startTimeSlot) || blockedTimes.includes(bookingData.startTime);
    if (!blocked) return;
    const newEndTime = calculateMinEndTime(nextAvailableStart);
    if (newEndTime) {
      onInputChange('startTime', nextAvailableStart);
      onInputChange('endTime', newEndTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when slot availability inputs change
  }, [
    bookingData.date,
    bookingData.bookingType,
    bookingData.startTime,
    startTimeSlot,
    blockedTimes,
    nextAvailableStart,
    bookingRules
  ]);

  // Min duration + first valid end slot not blocked (aligned with VenueBookingSidebar).
  useLayoutEffect(() => {
    if (!bookingData.startTime || !bookingData.endTime || loadingBusyTimes) return;
    const minEnd = calculateMinEndTime(bookingData.startTime);
    if (!minEnd) return;
    if (bookingData.endTime >= minEnd && !blockedEndTimes.includes(bookingData.endTime)) return;
    const firstValid = pickFirstValidEndAfterStart(bookingData.startTime, blockedEndTimes, minEnd);
    if (firstValid != null && firstValid !== bookingData.endTime) {
      onInputChange('endTime', firstValid);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onInputChange from parent
  }, [bookingData.startTime, bookingData.endTime, blockedEndTimes, loadingBusyTimes, minHours, onInputChange]);

  const handleStartTimeChange = (time) => {
    onInputChange('startTime', time);
  };

  const {
    hourlyRate,
    duration,
    cleaningFee,
    processingFee,
    platformFee,
    estimatedTotal,
    isDiscounted
  } = pricingDetails;

  // Match VenueHostInfo: same display name for message placeholder / copy
  const hostName = venue?.contact_name || venue?.contactName || venue?.name || 'the host';

  const formatPrice = (value) => {
    if (!Number.isFinite(value)) return '0.00';
    return Number(value).toFixed(2);
  };

  const hostRulesText = useMemo(() => getBookingHostRulesText(venue), [venue]);
  const hostRulesLines = useMemo(
    () =>
      hostRulesText
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean),
    [hostRulesText]
  );
  const cancellationPolicyText = useMemo(
    () => getBookingCancellationPolicyText(venue),
    [venue]
  );

  const hostRulesCollapsedText = useMemo(() => {
    if (!hostRulesText) return '';
    const maxChars = 220;
    if (hostRulesText.length <= maxChars) return hostRulesText;
    return `${hostRulesText.slice(0, maxChars).trimEnd()}...`;
  }, [hostRulesText]);

  const canExpandHostRules = hostRulesText.length > hostRulesCollapsedText.length;
  const useHostRulesList = hostRulesLines.length > 1;
  const visibleHostRulesLines =
    !useHostRulesList || showFullHostRules
      ? hostRulesLines
      : hostRulesLines.slice(0, 2);
  const canExpandHostRulesList =
    useHostRulesList && hostRulesLines.length > visibleHostRulesLines.length;
  const showHostRulesReadMore = canExpandHostRulesList || (!useHostRulesList && canExpandHostRules);

  const depositQuote = useMemo(
    () =>
      hidePrice
        ? null
        : computeVenueDepositQuote(
            venue,
            {
              venueSubtotal: pricingDetails.bookingSubtotal,
              estimatedTotal: pricingDetails.estimatedTotal,
            },
            stripePaymentsUiEnabled
          ),
    [
      venue,
      pricingDetails.bookingSubtotal,
      pricingDetails.estimatedTotal,
      hidePrice,
      stripePaymentsUiEnabled,
    ]
  );

  const depositDisclaimerLines = useMemo(
    () => bookingDepositDisclaimerLines(depositQuote),
    [depositQuote]
  );

  /** Logged-in user with an email: only phone is editable (name + email fixed). */
  const lockNameAndEmail =
    Boolean(currentUser) && Boolean(String(bookingData.email || '').trim());

  const hasText = (value) => String(value || '').trim().length > 0;
  const inHouseCatering = String(bookingData.catering || '').toLowerCase().includes('in-house');
  const eventDetailsFilled =
    hasText(bookingData.activity) &&
    hasText(bookingData.date) &&
    hasText(bookingData.startTime) &&
    hasText(bookingData.endTime) &&
    hasText(bookingData.catering) &&
    (venueSpaces.length === 0 || hasText(bookingData.spaceId)) &&
    Number.isFinite(attendeesCount) &&
    attendeesCount >= 1 &&
    (!bookingData.isRecurring || hasText(bookingData.recurringSchedule)) &&
    (!(inHouseCatering && bookingData.hasDietaryRestrictions) || hasText(bookingData.dietaryRestrictions));
  const organiserDetailsFilled =
    hasText(bookingData.name) && hasText(bookingData.email) && acceptTerms;
  const mandatoryFieldsFilled = eventDetailsFilled && organiserDetailsFilled;

  const stepValid = {
    [BOOKING_STEPS.EVENT]: eventDetailsFilled,
    [BOOKING_STEPS.ADDONS]: true,
    [BOOKING_STEPS.ORGANISER]: organiserDetailsFilled,
  };

  const handleSaveStep = (event) => {
    if (!stepValid[activeStep]) return;
    event.currentTarget.blur();
    setCompletedSteps((prev) => (prev.includes(activeStep) ? prev : [...prev, activeStep]));
    const index = BOOKING_STEP_ORDER.indexOf(activeStep);
    const next = BOOKING_STEP_ORDER[index + 1];
    if (next) {
      scrollStepIntoViewRef.current = true;
      setActiveStep(next);
    }
  };

  const renderStepActions = () => {
    const isLastStep = activeStep === BOOKING_STEP_ORDER[BOOKING_STEP_ORDER.length - 1];
    if (isLastStep) {
      return null;
    }
    return (
      <div className="booking-step-actions">
        <button
          type="button"
          className="booking-step-next"
          disabled={!stepValid[activeStep]}
          onClick={handleSaveStep}
        >
          Save and next
        </button>
      </div>
    );
  };

  return (
    <form id={BOOKING_FORM_ID} className="booking-form" onSubmit={onSubmit} noValidate>
      <div className="booking-steps" role="tablist" aria-label="Booking steps" ref={stepsRef}>
        {BOOKING_STEP_ORDER.map((step) => {
          const isActive = activeStep === step;
          const isComplete = completedSteps.includes(step) && stepValid[step];
          return (
            <button
              key={step}
              type="button"
              role="tab"
              id={`booking-step-tab-${step}`}
              aria-selected={isActive}
              aria-controls={`booking-step-panel-${step}`}
              className={`booking-step-tab${isActive ? ' is-active' : ''}`}
              onClick={() => setActiveStep(step)}
            >
              <span className="booking-step-tab-main">
                <span
                  className={`booking-step-dot${isComplete ? ' is-complete' : ''}`}
                  aria-label={isComplete ? 'Completed' : 'Incomplete'}
                />
                <span className="booking-step-label">{BOOKING_STEP_LABELS[step]}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div
        className="booking-step-panel"
        role="tabpanel"
        id={`booking-step-panel-${BOOKING_STEPS.EVENT}`}
        aria-labelledby={`booking-step-tab-${BOOKING_STEPS.EVENT}`}
        hidden={activeStep !== BOOKING_STEPS.EVENT}
      >
      <h2 className="booking-step-title">{BOOKING_STEP_TITLES[BOOKING_STEPS.EVENT]}</h2>
      <div className="form-section-booking">
        <div className="form-fields-group">
          <div className="form-group">
            <label className="form-label" htmlFor="booking-space">
              Space {venueSpaces.length > 0 && <span className="required-star">*</span>}
            </label>
            <SpaceSelect
              id="booking-space"
              spaces={venueSpaces}
              value={bookingData.spaceId || ''}
              onChange={(spaceId) => onInputChange('spaceId', spaceId)}
            />
          </div>
          <div className="form-row-fields">
            <div className="form-group form-group-half">
              <label className="form-label" htmlFor="event-activity">
                Activity <span className="required-star">*</span>
              </label>
              <div className="input-wrapper">
                <input
                  id="event-activity"
                  type="text"
                  value={bookingData.activity}
                  onChange={(event) => onInputChange('activity', event.target.value)}
                  className="form-input"
                  required
                />
              </div>
            </div>
            <div className="form-group form-group-half">
              <label className="form-label" htmlFor="event-attendees">
                Attendees <span className="required-star">*</span>
              </label>
              <div className="booking-guest-stepper">
                <input
                  id="event-attendees"
                  type="number"
                  min={1}
                  max={maxAttendees ?? undefined}
                  step={1}
                  value={bookingData.attendees}
                  onChange={(event) => {
                    const raw = event.target.value;
                    if (raw === '') {
                      onInputChange('attendees', '');
                      return;
                    }
                    const parsed = parseInt(raw, 10);
                    if (!Number.isFinite(parsed)) {
                      onInputChange('attendees', '');
                      return;
                    }
                    onInputChange('attendees', String(parsed));
                  }}
                  onBlur={() => {
                    const current = parseInt(bookingData.attendees, 10);
                    if (!Number.isFinite(current) || current < 1) return;
                    if (maxAttendees != null && current > maxAttendees) {
                      onInputChange('attendees', String(maxAttendees));
                    }
                  }}
                  placeholder="No. of guests"
                  className="form-input"
                  required
                />
                <div className="booking-guest-stepper-actions">
                  <button
                    type="button"
                    className="booking-guest-stepper-btn"
                    aria-label="Decrease attendees"
                    disabled={!bookingData.attendees || Number(bookingData.attendees) <= 1}
                    onClick={() => {
                      const current = parseInt(bookingData.attendees, 10);
                      if (!Number.isFinite(current) || current <= 1) return;
                      onInputChange('attendees', String(current - 1));
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M3.5 8H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="booking-guest-stepper-btn"
                    aria-label="Increase attendees"
                    disabled={maxAttendees != null && Number(bookingData.attendees) >= maxAttendees}
                    onClick={() => {
                      const current = parseInt(bookingData.attendees, 10);
                      const next = Number.isFinite(current) ? current + 1 : 1;
                      const capped = maxAttendees != null ? Math.min(next, maxAttendees) : next;
                      onInputChange('attendees', String(capped));
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M8 3.5V12.5M3.5 8H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">
              Date and time <span className="required-star">*</span>
            </label>
            <div className="booking-datetime">
              <div className="booking-datetime-date">
                <PriceCalendar
                  selectedDate={bookingData.date ? String(bookingData.date).split('T')[0] : ''}
                  onDateSelect={(dateStr) => onInputChange('date', dateStr)}
                  venue={venue}
                  dynamicPricing={dynamicPricing}
                  dynamicPricingPerPerson={dynamicPricingPerPerson}
                  defaultPerPersonPrice={defaultPerPersonPrice}
                  flatPricing={flatPricing}
                  minBookingHours={minHours}
                  bookingMode={isFullVenue ? 'exclusive' : 'per_person'}
                  showDiscountedRatesBanner={false}
                  hidePrice={hidePrice}
                  dropdownBelow
                  matchInputWidth
                />
              </div>
              <TimeRangePicker
                attached
                startId={startTimeInputId}
                endId={endTimeInputId}
                startTime={bookingData.startTime}
                endTime={bookingData.endTime}
                onStartChange={handleStartTimeChange}
                onEndChange={(time) => onInputChange('endTime', time)}
                startPlaceholder="Start time"
                endPlaceholder="End time"
                blockedTimes={blockedTimes}
                blockedEndTimes={blockedEndTimes}
                minEndTime={calculateMinEndTime(bookingData.startTime)}
                endDisabled={!bookingData.startTime}
              />
            </div>
            <div className="checkbox-group booking-recurring-card">
              <label className="booking-toggle-row">
                <span>This is a recurring booking</span>
                <input
                  type="checkbox"
                  role="switch"
                  checked={Boolean(bookingData.isRecurring)}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    onInputChange('isRecurring', checked);
                    if (!checked) {
                      onInputChange('recurringSchedule', '');
                    }
                  }}
                  className="booking-toggle-input"
                />
                <span className="booking-toggle" aria-hidden="true" />
              </label>
              {bookingData.isRecurring ? (
                <div className="form-group booking-recurring-schedule">
                  <label className="form-label" htmlFor="recurring-schedule">
                    Describe how often the booking repeats and for how long. <span className="required-star">*</span>
                  </label>
                  <textarea
                    id="recurring-schedule"
                    value={bookingData.recurringSchedule || ''}
                    onChange={(event) => onInputChange('recurringSchedule', event.target.value)}
                    placeholder="e.g. Every Tuesday for 8 weeks, 2 hours each session"
                    className="form-textarea-booking"
                    rows={3}
                    required
                  />
                  {errors.recurringSchedule ? (
                    <span className="error-message">{errors.recurringSchedule}</span>
                  ) : null}
                </div>
              ) : null}
            </div>
            {errors.date && <span className="error-message">{errors.date}</span>}
            {errors.duration && <span className="error-message">{errors.duration}</span>}
          </div>
          <p className="booking-form-eyebrow">Other requirements</p>
          <div className="radio-group">
            <label className="radio-label">
              What are your catering preferences <span className="required-star">*</span>
            </label>
            <div className="radio-options">
              {['In-house catering', 'External catering'].map((option) => (
                <label className="radio-option" key={option}>
                  <input
                    type="radio"
                    name="catering"
                    value={option}
                    checked={bookingData.catering === option}
                    onChange={(event) => onInputChange('catering', event.target.value)}
                    className="radio-input"
                  />
                  <span className="radio-custom" aria-hidden="true" />
                  {option.replace(' catering', '')}
                </label>
              ))}
            </div>
          </div>
          <div className="booking-toggle-group">
            <div className="checkbox-group">
              <label className="booking-toggle-row">
                <span>Do you need audio/visual (A/V)?</span>
                <input
                  type="checkbox"
                  role="switch"
                  checked={Boolean(bookingData.needsAv)}
                  onChange={(event) => onInputChange('needsAv', event.target.checked)}
                  className="booking-toggle-input"
                />
                <span className="booking-toggle" aria-hidden="true" />
              </label>
            </div>
            <div className="checkbox-group">
              <label className="booking-toggle-row">
                <span>Will alcohol be consumed?</span>
                <input
                  type="checkbox"
                  role="switch"
                  checked={bookingData.alcohol === 'Yes'}
                  onChange={(event) => onInputChange('alcohol', event.target.checked ? 'Yes' : 'No')}
                  className="booking-toggle-input"
                />
                <span className="booking-toggle" aria-hidden="true" />
              </label>
            </div>
            {bookingData.catering === 'In-house catering' ? (
              <div className="checkbox-group">
                <label className="booking-toggle-row">
                  <span>Are there dietary restrictions?</span>
                  <input
                    type="checkbox"
                    role="switch"
                    checked={Boolean(bookingData.hasDietaryRestrictions)}
                    onChange={(event) => onInputChange('hasDietaryRestrictions', event.target.checked)}
                    className="booking-toggle-input"
                  />
                  <span className="booking-toggle" aria-hidden="true" />
                </label>
              </div>
            ) : null}
          </div>
          {bookingData.catering === 'In-house catering' && bookingData.hasDietaryRestrictions ? (
            <div className="form-group">
              <label className="form-label" htmlFor="dietary-restrictions">
                Dietary restrictions <span className="required-star">*</span>
              </label>
              <textarea
                id="dietary-restrictions"
                value={bookingData.dietaryRestrictions || ''}
                onChange={(event) => onInputChange('dietaryRestrictions', event.target.value)}
                placeholder="Please share any dietary restrictions for your guests"
                className="form-textarea-booking"
                rows={3}
                required
              />
              {errors.dietaryRestrictions ? (
                <span className="error-message">{errors.dietaryRestrictions}</span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      {renderStepActions()}
      </div>

      <div
        className="booking-step-panel"
        role="tabpanel"
        id={`booking-step-panel-${BOOKING_STEPS.ADDONS}`}
        aria-labelledby={`booking-step-tab-${BOOKING_STEPS.ADDONS}`}
        hidden={activeStep !== BOOKING_STEPS.ADDONS}
      >
      <h2 className="booking-step-title">{BOOKING_STEP_TITLES[BOOKING_STEPS.ADDONS]}</h2>
      <div className="form-section-booking">
        <div className="form-fields-group">
          {hasAnyExtras ? (
            <>
              {visibleAddonTabs.length > 1 ? (
                <div className="booking-addon-tabs" role="tablist" aria-label="Extra item categories">
                  {hasPackages ? (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeAddonTab === ADDON_TABS.PACKAGES}
                      className={`booking-addon-tab${activeAddonTab === ADDON_TABS.PACKAGES ? ' is-active' : ''}`}
                      onClick={() => setActiveAddonTab(ADDON_TABS.PACKAGES)}
                    >
                      Packages
                    </button>
                  ) : null}
                  {hasAddOns ? (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeAddonTab === ADDON_TABS.ADDONS}
                      className={`booking-addon-tab${activeAddonTab === ADDON_TABS.ADDONS ? ' is-active' : ''}`}
                      onClick={() => setActiveAddonTab(ADDON_TABS.ADDONS)}
                    >
                      Items
                    </button>
                  ) : null}
                  {hasOthers ? (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeAddonTab === ADDON_TABS.OTHERS}
                      className={`booking-addon-tab${activeAddonTab === ADDON_TABS.OTHERS ? ' is-active' : ''}`}
                      onClick={() => setActiveAddonTab(ADDON_TABS.OTHERS)}
                    >
                      Others
                    </button>
                  ) : null}
                </div>
              ) : null}

              <div className="addons-list" role="tabpanel">
                {activeAddonTab === ADDON_TABS.PACKAGES
                  ? paginatedTabItems.map((pkg) => {
                      const packageKey = pkg.id;
                      const isSelectable = Boolean(packageKey);
                      const quantity = isSelectable ? (packageQuantities?.[packageKey] || 0) : 0;
                      const unitPrice = Number.isFinite(pkg.unitPrice) ? pkg.unitPrice : 0;
                      return (
                        <div className="addon-row" key={pkg.id || pkg.name}>
                          <div className="addon-name">{pkg.name}</div>
                          <div className="addon-price">£{formatPrice(unitPrice)} / person</div>
                          <div className="addon-controls">
                            <AddonQuantityStepper
                              value={quantity}
                              max={addonQtyMax}
                              disabled={!isSelectable}
                              ariaLabel={`${pkg.name} quantity`}
                              onChange={(next) => onPackageQuantityChange(packageKey, next)}
                            />
                          </div>
                        </div>
                      );
                    })
                  : null}

                {activeAddonTab === ADDON_TABS.ADDONS
                  ? paginatedTabItems.map((item) => {
                      const itemKey = item.id;
                      const isSelectable = Boolean(itemKey);
                      const quantity = isSelectable ? (addOnItemQuantities?.[itemKey] || 0) : 0;
                      const unitPrice = Number.isFinite(item.unitPrice) ? item.unitPrice : 0;
                      return (
                        <div className="addon-row" key={itemKey || item.name}>
                          <div className="addon-name">{item.name}</div>
                          <div className="addon-price">£{formatPrice(unitPrice)} / person</div>
                          <div className="addon-controls">
                            <AddonQuantityStepper
                              value={quantity}
                              max={addonQtyMax}
                              disabled={!isSelectable}
                              ariaLabel={`${item.name} quantity`}
                              onChange={(next) => onAddOnQuantityChange(itemKey, next)}
                            />
                          </div>
                        </div>
                      );
                    })
                  : null}

                {activeAddonTab === ADDON_TABS.OTHERS
                  ? paginatedTabItems.map((item) => {
                      const itemKey = item.id;
                      const isSelectable = Boolean(itemKey);
                      const quantity = isSelectable ? (otherItemQuantities?.[itemKey] || 0) : 0;
                      const unitPrice = Number.isFinite(item.unitPrice) ? item.unitPrice : 0;
                      const qtyMax = getOtherQtyMax(item);
                      const unitLabel = getAddonUnitLabel(item);
                      return (
                        <div className="addon-row" key={itemKey || item.name}>
                          <div className="addon-name">{item.name}</div>
                          <div className="addon-price">£{formatPrice(unitPrice)} {unitLabel}</div>
                          <div className="addon-controls">
                            <AddonQuantityStepper
                              value={quantity}
                              max={qtyMax}
                              disabled={!isSelectable}
                              ariaLabel={`${item.name} quantity`}
                              onChange={(next) => onOtherQuantityChange(itemKey, next)}
                            />
                          </div>
                        </div>
                      );
                    })
                  : null}
              </div>

              {activeTabItems.length === 0 && selectedTagSlug ? (
                <div className="addons-empty-state">No items match this tag in this category.</div>
              ) : null}

              {totalAddonPages > 1 ? (
                <div className="addons-pagination">
                  <Pagination
                    currentPage={addonPage}
                    totalPages={totalAddonPages}
                    startIndex={addonStartIndex}
                    endIndex={addonEndIndex}
                    totalItems={activeTabItems.length}
                    onPageChange={setAddonPage}
                    onPreviousPage={() => setAddonPage((page) => Math.max(1, page - 1))}
                    onNextPage={() => setAddonPage((page) => Math.min(totalAddonPages, page + 1))}
                  />
                </div>
              ) : null}
            </>
          ) : catalogLoading ? (
            <div className="addons-empty-state">Loading packages and items…</div>
          ) : (
            <div className="addons-empty-state">No packages or items available for this venue.</div>
          )}
        </div>
      </div>
      {renderStepActions()}
      </div>

      <div
        className="booking-step-panel"
        role="tabpanel"
        id={`booking-step-panel-${BOOKING_STEPS.ORGANISER}`}
        aria-labelledby={`booking-step-tab-${BOOKING_STEPS.ORGANISER}`}
        hidden={activeStep !== BOOKING_STEPS.ORGANISER}
      >
      <h2 className="booking-step-title">{BOOKING_STEP_TITLES[BOOKING_STEPS.ORGANISER]}</h2>
      {currentUser ? (
        <div className="auto-fill-notification">
          <div className="auto-fill-icon" aria-hidden="true">
            <CheckCircleIcon />
          </div>
          <span className="auto-fill-text">
            {lockNameAndEmail
              ? 'Your name and email come from your account and can’t be changed here. You can update your phone number if needed.'
              : 'Your details have been automatically filled in. You can edit them if needed.'}
          </span>
        </div>
      ) : null}

      <div className="form-section-booking">
        <div className="form-fields-group">
          <div className="form-group">
            <label className="form-label" htmlFor="organiser-name">
              Name <span className="required-star">*</span>
            </label>
            <div className={`input-wrapper ${errors.name ? 'error' : ''}`}>
              <input
                id="organiser-name"
                type="text"
                value={bookingData.name}
                onChange={(event) => onInputChange('name', event.target.value)}
                placeholder="Enter your name"
                className={`form-input${lockNameAndEmail ? ' form-input-readonly' : ''}`}
                readOnly={lockNameAndEmail}
                required
              />
            </div>
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>
          <div className="form-row-fields">
            <div className="form-group form-group-half">
              <label className="form-label" htmlFor="organiser-email">
                Email <span className="required-star">*</span>
              </label>
              <div className={`input-wrapper ${errors.email ? 'error' : ''}`}>
                <input
                  id="organiser-email"
                  type="email"
                  value={bookingData.email}
                  onChange={(event) => onInputChange('email', event.target.value)}
                  placeholder="Enter your email"
                  className={`form-input${lockNameAndEmail ? ' form-input-readonly' : ''}`}
                  readOnly={lockNameAndEmail}
                  required
                />
              </div>
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
            <div className="form-group form-group-half">
              <label className="form-label" htmlFor="organiser-phone">
                Phone (optional)
              </label>
              <div className={`booking-phone-row ${errors.phone ? 'error' : ''}`}>
                <span className="booking-phone-prefix" aria-hidden="true">🇬🇧 +44</span>
                <input
                  id="organiser-phone"
                  type="tel"
                  value={String(bookingData.phone || '').replace(/^\+44\s*/, '')}
                  onChange={(event) => {
                    const localNumber = event.target.value.replace(/^\+44\s*/, '');
                    onInputChange('phone', localNumber ? `+44 ${localNumber}` : '');
                  }}
                  placeholder="20 7123 4567"
                  className="booking-phone-input"
                />
              </div>
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>
          </div>
          {!lockNameAndEmail ? (
            <p className="booking-email-info-banner">
              Bookings are linked to this email. To view or pay later, sign in with{' '}
              <strong>this same address</strong> (including if you use Google sign-in).
            </p>
          ) : null}
        </div>
      </div>

      <div className="form-section-booking">
        <h3 className="section-title-booking-page">Your host</h3>
        <div className="form-fields-group">
          {venue ? <VenueHostInfo venue={venue} /> : null}
          <div className="form-group">
            <textarea
              value={bookingData.message}
              onChange={(event) => onInputChange('message', event.target.value)}
              placeholder={`Introduce yourself to ${hostName} and tell her more about your requirements`}
              className="form-textarea-booking"
            />
          </div>
          <div className="host-rules-section">
            <h4 className="host-rules-title">Review host rules</h4>
            {useHostRulesList ? (
              <ul className="host-rules-list">
                {visibleHostRulesLines.map((line) => (
                  <li key={line} className="host-rules-item">
                    <BulletSparkIcon className="host-rules-bullet" />
                    <span className="host-rules-text">{line.replace(/^\s*[-–—•*]\s+/, '')}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="host-rules-text">
                {showFullHostRules ? hostRulesText : hostRulesCollapsedText}
              </p>
            )}
            {showHostRulesReadMore ? (
              <button
                type="button"
                className="host-rules-read-more"
                onClick={() => setShowFullHostRules((prev) => !prev)}
              >
                {showFullHostRules ? 'Show less' : 'Read more'}
              </button>
            ) : null}
          </div>
          {cancellationPolicyText ? (
            <div className="host-rules-section">
              <h4 className="host-rules-title">Cancellation policy</h4>
              <p className="host-rules-text">{cancellationPolicyText}</p>
            </div>
          ) : null}
          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                className="checkbox-input"
                checked={acceptTerms}
                onChange={(event) => onAcceptTermsChange(event.target.checked)}
                required
              />
              <span className="checkbox-custom" aria-hidden="true" />
              I accept and understand the host rules and disclosures.
            </label>
            {errors.terms && <span className="error-message">{errors.terms}</span>}
          </div>
        </div>
      </div>
      {renderStepActions()}
      </div>

      {hidePrice ? (
        <div className="cost-summary-mobile" style={{ padding: '16px 0', color: '#585858', fontSize: '14px' }}>
          Contact venue for pricing
        </div>
      ) : (
      <CostSummary
        className="cost-summary-mobile"
        hourlyRate={hourlyRate}
        duration={duration}
        baseFee={pricingDetails.baseFee}
        cleaningFee={cleaningFee}
        processingFee={processingFee}
        platformFee={platformFee}
        venueSubtotal={pricingDetails.bookingSubtotal}
        estimatedTotal={estimatedTotal}
        isDiscounted={isDiscounted}
        title="Cost Summary"
        showBreakdown
        discountAmount={pricingDetails.discountAmount}
        discountCode={pricingDetails.discountCode}
        addOnsTotal={pricingDetails.addOnsTotal}
        addOns={pricingDetails.selectedAddOns}
        isPerPerson={pricingDetails.isPerPerson}
        tableCount={pricingDetails.tableCount}
        perPersonRate={pricingDetails.perPersonRate}
        usingFlatPricing={pricingDetails.usingFlatPricing}
        matchingFlatRule={pricingDetails.matchingFlatRule}
        depositQuote={depositQuote}
      />
      )}

      {(() => {
        const submitSection = (
      <div className="submit-section-booking">
        {userFacingBlockingReasons.length > 0 && (
          <div className="submit-error-message" style={{ 
            color: '#d32f2f', 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: '#ffebee', 
            borderRadius: '4px',
            fontSize: '14px'
          }}>
            {userFacingBlockingReasons.map((r) => {
              const s = String(r);
              if (s === 'overlapping_booking' || s.toLowerCase().includes('overlapping')) {
                return bookingData.bookingType === 'full-venue'
                  ? 'Full venue hire is not available while another booking overlaps this time'
                  : 'Space booked for this start time';
              }
              if (s === 'outside_operating_hours') return 'This time is outside our opening hours';
              return s;
            }).join(', ')}
          </div>
        )}
        {submitError && (
          <div className="submit-error-message" style={{ 
            color: '#d32f2f', 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: '#ffebee', 
            borderRadius: '4px',
            fontSize: '14px'
          }}>
            {(submitError === 'overlapping_booking' || String(submitError).toLowerCase().includes('overlapping_booking'))
              ? 'space booked for this start time'
              : submitError}
          </div>
        )}
        <button
          type="submit"
          form={BOOKING_FORM_ID}
          className="submit-button-booking"
          disabled={isSubmitting || cannotBookThisSlot || !mandatoryFieldsFilled}
        >
          {isSubmitting ? 'Submitting…' : 'Request to book'}
        </button>
        <p className="disclaimer-booking">
          {depositDisclaimerLines[0]}
          <br />
          {depositDisclaimerLines[1]}
        </p>
      </div>
        );
        return submitSlot ? createPortal(submitSection, submitSlot) : submitSection;
      })()}
    </form>
  );
};

BookingForm.propTypes = {
  acceptTerms: PropTypes.bool.isRequired,
  availabilityPreview: PropTypes.shape({
    is_available: PropTypes.bool,
    blocking_reasons: PropTypes.arrayOf(PropTypes.string),
    remaining_capacity: PropTypes.number,
    capacity_total: PropTypes.number
  }),
  bookingData: PropTypes.shape({
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    startTime: PropTypes.string.isRequired,
    endTime: PropTypes.string.isRequired,
    spaceId: PropTypes.string,
    bookingType: PropTypes.string,
    tableCount: PropTypes.number,
    isFlexible: PropTypes.bool.isRequired,
    isRecurring: PropTypes.bool,
    recurringSchedule: PropTypes.string,
    activity: PropTypes.string.isRequired,
    attendees: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    layout: PropTypes.string.isRequired,
    needsAv: PropTypes.bool,
    alcohol: PropTypes.string.isRequired,
    catering: PropTypes.string.isRequired,
    hasDietaryRestrictions: PropTypes.bool,
    dietaryRestrictions: PropTypes.string,
    message: PropTypes.string.isRequired
  }).isRequired,
  currentUser: PropTypes.shape({
    uid: PropTypes.string
  }),
  errors: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    terms: PropTypes.string,
    date: PropTypes.string,
    duration: PropTypes.string,
    recurringSchedule: PropTypes.string,
    dietaryRestrictions: PropTypes.string
  }).isRequired,
  submitError: PropTypes.string,
  isSubmitting: PropTypes.bool.isRequired,
  onAcceptTermsChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  pricingDetails: PropTypes.shape({
    hourlyRate: PropTypes.number.isRequired,
    duration: PropTypes.number.isRequired,
    cleaningFee: PropTypes.number.isRequired,
    processingFee: PropTypes.number.isRequired,
    platformFee: PropTypes.number,
    estimatedTotal: PropTypes.number.isRequired,
    isDiscounted: PropTypes.bool.isRequired,
    baseHourlyRate: PropTypes.number,
    bookingSubtotal: PropTypes.number,
    discountAmount: PropTypes.number,
    discountCode: PropTypes.string,
    addOnsTotal: PropTypes.number,
    selectedAddOns: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      quantity: PropTypes.number,
      unitPrice: PropTypes.number,
      total: PropTypes.number
    }))
  }).isRequired,
  venue: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    contact: PropTypes.string,
    contactName: PropTypes.string,
    contact_name: PropTypes.string,
    name: PropTypes.string
  }),
  venueSpaces: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    space_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    space_name: PropTypes.string
  })),
  packages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    price: PropTypes.number,
    unitPrice: PropTypes.number
  })),
  packageQuantities: PropTypes.object,
  onPackageQuantityChange: PropTypes.func,
  addOnItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    unitPrice: PropTypes.number,
    pricing_unit: PropTypes.string
  })),
  addOnItemQuantities: PropTypes.object,
  onAddOnQuantityChange: PropTypes.func,
  otherItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    unitPrice: PropTypes.number,
    pricing_unit: PropTypes.string
  })),
  otherItemQuantities: PropTypes.object,
  onOtherQuantityChange: PropTypes.func,
  bookingRules: PropTypes.shape({
    min_hours_exclusive: PropTypes.number,
    min_hours_per_person: PropTypes.number,
    min_total_hours: PropTypes.number
  }),
  dynamicPricing: PropTypes.array,
  dynamicPricingPerPerson: PropTypes.array,
  defaultPerPersonPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  flatPricing: PropTypes.array,
  catalogTags: PropTypes.arrayOf(PropTypes.shape({
    slug: PropTypes.string,
    label: PropTypes.string,
    is_active: PropTypes.bool,
  })),
  catalogLoading: PropTypes.bool,
  submitSlot: PropTypes.instanceOf(typeof Element === 'undefined' ? Object : Element),
  stripePaymentsUiEnabled: PropTypes.bool,
};

BookingForm.defaultProps = {
  availabilityPreview: null,
  currentUser: null,
  venue: null,
  venueSpaces: [],
  submitError: '',
  packages: [],
  packageQuantities: {},
  onPackageQuantityChange: () => {},
  addOnItems: [],
  addOnItemQuantities: {},
  onAddOnQuantityChange: () => {},
  otherItems: [],
  otherItemQuantities: {},
  onOtherQuantityChange: () => {},
  bookingRules: null,
  catalogTags: [],
  catalogLoading: false,
  submitSlot: null,
};

export default BookingForm;
